// src/components/PaymentsDeposits.js — central Payments screen + Make Deposits
// [marker: payments-deposits v1]
// • Payments to Deposit: every check, cash and card payment received, waiting to go to the bank
// • Make Deposit (QuickBooks style): tick payments, pick the bank, add fees/other lines → deposit
// • Deposits: history; open one to add/remove payments or delete it
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import DataGrid from './DataGrid';
import { AccountSelect, useAccounts } from './AccountSelect';
import { ClassSelect } from './ClassSelect'; // [marker: books-pro v1] class on other deposit lines
import ReceivePaymentModal, { METHOD_LABELS, fmtDay, money, r2, todayStr } from './ReceivePaymentModal';

const dayGet = (k) => (r) => (r[k] ? String(r[k]).slice(0, 10) + 'T00:00:00' : null);
const SOURCE_LABELS = { manual: 'Entered here', invoice_auto: 'Invoice payment', ticket_order: 'Event tickets', sponsorship: 'Sponsorship', refund: 'Card refund', historical: 'Before tracking' };

export default function PaymentsDeposits({ canEdit, onChanged }) {
  const [view, setView] = useState('pending'); // pending | current | deposited | historical | deposits
  const [rows, setRows] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState([]);
  const [receiving, setReceiving] = useState(null); // {} new | {id} edit
  const [depositing, setDepositing] = useState(null); // { ids } new | { id } edit

  const load = useCallback(async () => {
    setLoading(true);
    try {
      api.get('/receivables/summary').then(r => setSummary(r.data)).catch(() => {});
      if (view === 'deposits') setDeposits((await api.get('/receivables/deposits')).data || []);
      else setRows((await api.get(`/receivables/receipts?view=${view}`)).data || []);
    } catch { toast.error('Could not load payments.'); }
    finally { setLoading(false); }
  }, [view]);
  useEffect(() => { load(); setSelected([]); }, [load]);

  const changed = () => { load(); setSelected([]); onChanged && onChanged(); };
  const selTotal = r2(rows.filter(r => selected.includes(String(r.id))).reduce((s, r) => s + parseFloat(r.amount), 0));

  const receiptColumns = [
    { key: 'received_date', label: 'Received', width: 105, type: 'date', get: dayGet('received_date') },
    { key: 'payer', label: 'Received From', width: 210, get: r => r.payer_name || r.business_name || '' },
    { key: 'method', label: 'Method', width: 115, text: r => METHOD_LABELS[r.method] || r.method },
    { key: 'check_number', label: 'Check #', width: 85 },
    { key: 'amount', label: 'Amount', width: 110, type: 'money', align: 'right' },
    { key: 'applied', label: 'Applied To', width: 200, text: r => (r.applied || []).map(a => a.invoice_number).join(', ') || (r.other_account_name ? `→ ${r.other_account_name}` : '') },
    { key: 'memo', label: 'Memo', width: 200 },
    { key: 'status', label: 'Status', width: 105, text: r => ({ pending: 'To deposit', deposited: 'Deposited', historical: 'Older' }[r.status] || r.status),
      render: r => <span className={`badge ${r.status === 'pending' ? 'badge-pending' : r.status === 'deposited' ? 'badge-paid' : 'badge-muted'}`}>{({ pending: 'To deposit', deposited: 'Deposited', historical: 'Older' }[r.status] || r.status)}</span> },
    { key: 'deposited_date', label: 'Deposit Date', width: 105, type: 'date', get: dayGet('deposited_date') },
    { key: 'deposit_bank', label: 'Deposited To', width: 170, get: r => r.deposit_account_name || r.deposit_to_account_name || '' },
    { key: 'source', label: 'From Where', width: 130, text: r => SOURCE_LABELS[r.source] || r.source, defaultHidden: true },
    { key: 'reference', label: 'Reference', width: 140, defaultHidden: true },
    { key: 'business_name', label: 'Business', width: 180, defaultHidden: true },
  ];

  const depositColumns = [
    { key: 'deposit_date', label: 'Deposit Date', width: 110, type: 'date', get: dayGet('deposit_date') },
    { key: 'bank_name', label: 'Bank Account', width: 220 },
    { key: 'payment_count', label: 'Payments', width: 90, type: 'number' },
    { key: 'other_count', label: 'Other Lines', width: 95, type: 'number' },
    { key: 'total', label: 'Total', width: 120, type: 'money', align: 'right' },
    { key: 'memo', label: 'Memo', width: 260 },
    { key: 'created_by', label: 'Made By', width: 140, get: d => [d.created_by_first_name, d.created_by_last_name].filter(Boolean).join(' ') },
  ];

  return (
    <div>
      {summary && (
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, fontSize: '0.85rem' }}>
          <span style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 10px' }}>
            <strong>{summary.pendingCount}</strong> payment{summary.pendingCount === 1 ? '' : 's'} waiting to be deposited — <strong>{money(summary.pendingTotal)}</strong>
          </span>
          <span style={{ color: 'var(--gray-500)' }}>
            The app keeps the books from <strong>{fmtDay(summary.booksStart)}</strong>; earlier history comes from QuickBooks.
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {[['pending', 'Payments to Deposit'], ['current', 'All Payments'], ['deposited', 'Deposited'], ['deposits', 'Deposits'], ['historical', 'Older Payments']].map(([k, l]) => (
          <button key={k} className={`btn btn-sm ${view === k ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setView(k)}>{l}</button>
        ))}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
          {canEdit && <button className="btn btn-secondary btn-sm" onClick={() => setReceiving({})}>+ Receive Payment</button>}
          {canEdit && view !== 'deposits' && (
            <button className="btn btn-primary btn-sm" onClick={() => setDepositing({ ids: selected })}>
              🏦 Make Deposit{selected.length ? ` (${selected.length} · ${money(selTotal)})` : '…'}
            </button>
          )}
        </span>
      </div>

      {view === 'deposits' ? (
        <DataGrid gridKey="deposits" rows={deposits} loading={loading} columns={depositColumns}
                  onRowClick={d => setDepositing({ id: d.id })} totals={['total']} emptyText="No deposits yet."
                  height="calc(100vh - 360px)" />
      ) : (
        <DataGrid gridKey={`receipts-${view === 'pending' ? 'pending' : 'all'}`} rows={rows} loading={loading} columns={receiptColumns}
                  onRowClick={r => setReceiving({ id: r.id })} totals={['amount']}
                  selectable={canEdit && view === 'pending'} selectedIds={selected} onSelectionChange={setSelected}
                  emptyText={view === 'pending' ? 'Nothing waiting — every payment received has been deposited.' : 'No payments.'}
                  height="calc(100vh - 360px)" />
      )}
      {view === 'pending' && (
        <p className="form-hint" style={{ marginTop: 8 }}>
          Tick the payments that went to the bank together (one deposit slip, or one card-processor payout), then Make Deposit.
          The deposit date fills in on each payment.
        </p>
      )}

      {receiving && (
        <ReceivePaymentModal receiptId={receiving.id} onClose={() => setReceiving(null)}
                             onSaved={() => { setReceiving(null); changed(); }} />
      )}
      {depositing && (
        <MakeDepositModal depositId={depositing.id} preselected={depositing.ids || []} canEdit={canEdit}
                          onClose={() => setDepositing(null)} onSaved={() => { setDepositing(null); changed(); }} />
      )}
    </div>
  );
}

// ─── Make Deposit ─────────────────────────────────────────────────────────────
export function MakeDepositModal({ depositId, preselected = [], canEdit = true, onClose, onSaved }) {
  const isEdit = !!depositId;
  const { accounts } = useAccounts();
  const banks = accounts.filter(a => a.account_type === 'bank');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState([]);   // payments shown (this deposit's + waiting ones)
  const [ticked, setTicked] = useState(new Set(preselected.map(String)));
  const [form, setForm] = useState({ depositDate: todayStr(), bankAccountId: null, memo: '' });
  const [others, setOthers] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const pending = (await api.get('/receivables/receipts?view=pending')).data || [];
        if (isEdit) {
          const d = (await api.get(`/receivables/deposits/${depositId}`)).data;
          setForm({ depositDate: d.deposit_date, bankAccountId: d.bank_account_id, memo: d.memo || '' });
          setList([...(d.receipts || []), ...pending]);
          setTicked(new Set((d.receipts || []).map(r => String(r.id))));
          setOthers((d.other_lines || []).map(o => ({ accountId: o.account_id, classId: o.class_id || null, receivedFrom: o.received_from || '', description: o.description || '', amount: String(r2(o.amount)) })));
        } else {
          setList(pending);
        }
      } catch { toast.error('Could not load payments.'); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!form.bankAccountId && banks.length) set('bankAccountId', banks[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banks.length]);

  const toggle = (id) => setTicked(t => { const n = new Set(t); const k = String(id); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const allOn = list.length > 0 && list.every(r => ticked.has(String(r.id)));
  const payTotal = r2(list.filter(r => ticked.has(String(r.id))).reduce((s, r) => s + parseFloat(r.amount), 0));
  const otherTotal = r2(others.reduce((s, o) => s + (parseFloat(o.amount) || 0), 0));
  const total = r2(payTotal + otherTotal);
  const byMethod = {};
  list.filter(r => ticked.has(String(r.id))).forEach(r => { const k = METHOD_LABELS[r.method] || r.method; byMethod[k] = r2((byMethod[k] || 0) + parseFloat(r.amount)); });

  const setOther = (i, k, v) => setOthers(o => o.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const save = async () => {
    if (!form.bankAccountId) return toast.error('Choose the bank account.');
    if (!ticked.size && !others.length) return toast.error('Tick at least one payment.');
    for (const o of others) {
      if (!o.accountId) return toast.error('Each extra line needs an account.');
      if (!parseFloat(o.amount)) return toast.error('Each extra line needs an amount.');
    }
    setSaving(true);
    try {
      const body = { ...form, receiptIds: Array.from(ticked).map(x => parseInt(x, 10)), otherLines: others.map(o => ({ ...o, amount: r2(o.amount) })) };
      if (isEdit) await api.patch(`/receivables/deposits/${depositId}`, body);
      else await api.post('/receivables/deposits', body);
      toast.success(isEdit ? 'Deposit updated.' : `Deposit of ${money(total)} recorded.`);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save the deposit.');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Delete this deposit? Its payments go back to Payments to Deposit.')) return;
    setSaving(true);
    try {
      const r = await api.delete(`/receivables/deposits/${depositId}`);
      toast.success(r.data?.message || 'Deposit removed.');
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete the deposit.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1400 }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 960 }}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Deposit' : 'Make Deposits'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {loading ? <div className="loading-page" style={{ minHeight: 200 }}><div className="spinner" /></div> : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Deposit To *</label>
                  <AccountSelect accounts={banks} value={form.bankAccountId} onChange={v => set('bankAccountId', v)} placeholder="— Bank account —" />
                </div>
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input className="form-input" type="date" value={form.depositDate} onChange={e => set('depositDate', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Memo</label>
                  <input className="form-input" value={form.memo} onChange={e => set('memo', e.target.value)} placeholder="e.g. Deposit slip 10/16" />
                </div>
              </div>

              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', margin: '4px 0 6px' }}>Select the payments to deposit</div>
              <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, maxHeight: 300, overflow: 'auto' }}>
                {list.length === 0 ? (
                  <div style={{ padding: 14, fontSize: '0.85rem', color: 'var(--gray-500)' }}>No payments are waiting to be deposited.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, background: 'var(--gray-50)' }}>
                        <th style={th}><input type="checkbox" checked={allOn} onChange={() => setTicked(allOn ? new Set() : new Set(list.map(r => String(r.id))))} /></th>
                        <th style={th}>Date</th><th style={th}>Method</th><th style={th}>Check #</th><th style={th}>Received From</th>
                        <th style={th}>Applied To</th><th style={{ ...th, textAlign: 'right' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map(r => {
                        const on = ticked.has(String(r.id));
                        return (
                          <tr key={r.id} onClick={() => toggle(r.id)} style={{ cursor: 'pointer', borderTop: '1px solid var(--gray-100)', background: on ? '#eff6ff' : undefined }}>
                            <td style={td}><input type="checkbox" checked={on} onChange={() => toggle(r.id)} onClick={e => e.stopPropagation()} /></td>
                            <td style={td}>{fmtDay(r.received_date)}</td>
                            <td style={td}>{METHOD_LABELS[r.method] || r.method}</td>
                            <td style={td}>{r.check_number || ''}</td>
                            <td style={td}>{r.payer_name || r.business_name || ''}</td>
                            <td style={{ ...td, color: 'var(--gray-500)' }}>{(r.applied || []).map(a => a.invoice_number).join(', ') || r.memo || ''}</td>
                            <td style={{ ...td, textAlign: 'right', color: parseFloat(r.amount) < 0 ? 'var(--danger)' : undefined }}>{money(r.amount)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '6px 2px', color: 'var(--gray-600)', flexWrap: 'wrap', gap: 8 }}>
                <span>{Object.entries(byMethod).map(([k, v]) => `${k}: ${money(v)}`).join(' · ') || 'Nothing ticked'}</span>
                <span>{ticked.size} selected — <strong>{money(payTotal)}</strong></span>
              </div>

              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', margin: '10px 0 6px' }}>Other deposits / fees (optional)</div>
              {others.map((o, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.1fr 1.5fr 1.2fr 1.4fr 0.9fr auto', gap: 6, marginBottom: 6 }}>
                  <input className="form-input" placeholder="Received from" value={o.receivedFrom} onChange={e => setOther(i, 'receivedFrom', e.target.value)} />
                  <AccountSelect value={o.accountId} onChange={v => setOther(i, 'accountId', v)} placeholder="— Account —" />
                  <ClassSelect value={o.classId} onChange={v => setOther(i, 'classId', v)} />
                  <input className="form-input" placeholder="Description" value={o.description} onChange={e => setOther(i, 'description', e.target.value)} />
                  <input className="form-input" type="number" step="0.01" placeholder="Amount" value={o.amount} style={{ textAlign: 'right' }} onChange={e => setOther(i, 'amount', e.target.value)} />
                  <button className="btn btn-ghost btn-sm" onClick={() => setOthers(x => x.filter((_, j) => j !== i))} title="Remove line">✕</button>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setOthers(x => [...x, { accountId: null, receivedFrom: '', description: '', amount: '' }])}>+ Add line</button>
                <button className="btn btn-ghost btn-sm" onClick={() => {
                  const fee = accounts.find(a => a.account_type === 'expense' && /credit card fee/i.test(a.name));
                  setOthers(x => [...x, { accountId: fee ? fee.id : null, receivedFrom: '', description: 'Card processing fees', amount: '' }]);
                }}>+ Card fees</button>
                <span className="form-hint" style={{ margin: 0 }}>Enter fees or cash back as a <strong>negative</strong> amount.</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14, fontSize: '1rem' }}>
                <span>Deposit Total: <strong style={{ fontSize: '1.15rem' }}>{money(total)}</strong></span>
              </div>
            </>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
          {isEdit && canEdit ? <button className="btn btn-danger" onClick={remove} disabled={saving}>Delete Deposit</button> : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            {canEdit && <button className="btn btn-primary" onClick={save} disabled={saving || loading}>{saving ? 'Saving…' : (isEdit ? 'Save Deposit' : 'Save Deposit')}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

const th = { textAlign: 'left', padding: '6px 8px', fontSize: '0.72rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', borderBottom: '1px solid var(--gray-200)' };
const td = { padding: '6px 8px', verticalAlign: 'middle' };
